<template>
  <div class="instructions">
    <div class="instructions__header">API Access</div>
    <div class="instructions__description">
      Use this code snippet in your project to use V7’s API
    </div>

    <RadioGroup
      class="instructions__radio-group"
      :options="options"
      :value="language"
      size="md"
      @change="setLanguage"
    />

    <CodeBlock
      :command="code"
      :wraps="true"
      @copy:success="onSuccessfulCopy"
      @copy:failure="onFailedCopy"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: 'AccessInstructions',
}
</script>

<script lang="ts" setup>
/**
 * Renders code snippets as examples on how to use an API key
 *
 * Used both in the general UI in the settings dialog, as well as the model specific UI on /models.
 *
 * The two use-cases use different sets of snippets.
 */
import { ref, computed } from 'vue'

import RadioGroup from '@/uiKit/RadioGroup/RadioGroup.vue'
import CodeBlock from '@/uiKit/CodeBlock/CodeBlock.vue'
import { useCodeBlock } from '@/uiKit/CodeBlock/useCodeBlock'

import { SupportedLanguages } from './types'
import type { AccessCodeExample } from './types'

const LANGUAGE_LABELS: Record<SupportedLanguages, string> = {
  [SupportedLanguages.cli]: 'CLI',
  [SupportedLanguages.elixir]: 'Elixir',
  [SupportedLanguages.javascript]: 'JavaScript',
  [SupportedLanguages.python]: 'Python',
  [SupportedLanguages.shell]: 'Shell',
}
const { onSuccessfulCopy, onFailedCopy } = useCodeBlock()

const props = defineProps<{
  snippets: AccessCodeExample[]
}>()

/** Currently selected language, updated when TabSelector component emits a change */
const language = ref<SupportedLanguages>(SupportedLanguages.cli)
const setLanguage = (value: string): void => {
  language.value = value as SupportedLanguages
}

/** Defines tabs for the tab selector component */
const options = computed(() =>
  props.snippets.map((s) => ({ id: s.language, text: LANGUAGE_LABELS[s.language] })),
)

/** Code snippet (object with language and code) for the currently selected tab */
const snippet = computed(() => props.snippets.find((s) => s.language === language.value) || null)

/** Content of the code snippet, defaulting to '' to satisfy typescript */
const code = computed(() => snippet.value?.code || '')
</script>

<style lang="scss" scoped>
.instructions {
  @include col;
  border-radius: 16px;
  background-color: var(--color-surface-tertiary);
  padding: 8px;
}

.instructions__header {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-top: 4px;
  margin-left: 12px;
}

.instructions__description {
  @include typographyRegularBody200;
  margin-bottom: 4px;
  margin-left: 12px;
  color: var(--color-text-subtlest);
}

.instructions__radio-group {
  margin-bottom: 8px;
}
</style>
